import { Suspense } from 'react';

import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconCopyOutline, IconAlertTriangle } from '@iwoca/icons';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { useFetchCreditCardBankDetails } from '../../hooks/useGetCreditCardBankDetails';
import { useGetCreditCardDashboardData } from '../useGetCreditCardDashboardData';

const CopyIcon = ({
    value,
    ariaLabel
}: {
    value: string;
    ariaLabel: string;
}) => (
    <button
        onClick={() =>
            // not compatible with IE9
            // eslint-disable-next-line compat/compat
            navigator.clipboard.writeText(value)
        }
        className="cursor-pointer border-0 bg-transparent p-0"
        aria-label={ariaLabel}
    >
        <IconCopyOutline className="fill-primary-10" />
    </button>
);

const PaymentDetailsBox = () => {
    const bankDetails = useFetchCreditCardBankDetails();

    if (!bankDetails) {
        return null;
    }

    return (
        <div className="mt-2xl rounded-l bg-primary-97 p-xl">
            <Text>
                <FormattedMessage
                    id="_.credit_card.dashboard.make_payment.transfer_instructions"
                    defaultMessage="Transfer to the following details to make a payment:"
                />
            </Text>
            <div className="mt-xl">
                <Text>
                    <FormattedMessage
                        id="_.credit_card.dashboard.make_payment.account_details_label"
                        defaultMessage="Account number"
                    />
                </Text>
                <div className="flex items-center justify-between">
                    {bankDetails.account_number}
                    <CopyIcon
                        value={bankDetails.account_number!}
                        ariaLabel="Copy account number"
                    />
                </div>
            </div>
            <div className="mt-xl">
                <Text>
                    <FormattedMessage
                        id="_.credit_card.dashboard.make_payment.sort_code_label"
                        defaultMessage="Sort code"
                    />
                </Text>
                <div className="flex items-center justify-between">
                    {bankDetails.sort_code}
                    <CopyIcon
                        value={bankDetails.sort_code!}
                        ariaLabel="Copy sort code"
                    />
                </div>
            </div>
            <div className="mt-xl">
                <Text>
                    <FormattedMessage
                        id="_.credit_card.dashboard.make_payment.payee_name_label"
                        defaultMessage="Payee name"
                    />
                </Text>
                <div className="flex items-center justify-between">
                    {bankDetails.owner_name}
                    <CopyIcon
                        value={bankDetails.owner_name!}
                        ariaLabel="Copy payee name"
                    />
                </div>
            </div>
            <div className="mt-xl">
                <Text>
                    <FormattedMessage
                        id="_.credit_card.dashboard.make_payment.payment_reference_label"
                        defaultMessage="Reference"
                    />
                </Text>
                <div className="flex items-center justify-between">
                    {bankDetails.reference}
                    <CopyIcon
                        value={bankDetails.reference!}
                        ariaLabel="Copy reference"
                    />
                </div>
            </div>
            <div className="items-top mt-2xl flex rounded-l border border-warning bg-warning-lt p-l">
                <IconAlertTriangle className="mr-s size-2xl" />
                <Text className="text-s">
                    <FormattedMessage
                        id="_.credit_card.dashboard.make_payment.warning"
                        defaultMessage="Please ensure all details are correct in order for us to assign it to your account."
                    />
                </Text>
            </div>
        </div>
    );
};

const PaymentAmounts = () => {
    const { repaymentAmount, totalOutstandingBalance } =
        useGetCreditCardDashboardData();
    return (
        <div
            className="mt-l flex flex-col justify-between gap-l min-m:flex-row min-m:gap-0"
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            aria-label="make-payment-amounts"
        >
            <div>
                <Text className="text-s min-m:text-m">
                    <FormattedMessage
                        id="_.credit_card.dashboard.make_payment.total_balance"
                        defaultMessage="Total account balance"
                    />
                </Text>
                <div className="text-xl font-bold min-m:text-2xl">
                    <FormattedCurrency value={totalOutstandingBalance} />
                </div>
            </div>
            <div className="hidden border-l border-secondary-90 min-m:block"></div>
            <div>
                <Text className="text-s min-m:text-m">
                    <FormattedMessage
                        id="_.credit_card.dashboard.make_payment.minimum_payment"
                        defaultMessage="Minimum payment due"
                    />
                </Text>
                <div className="text-xl font-bold min-m:text-2xl">
                    <FormattedCurrency value={repaymentAmount} />
                </div>
            </div>
        </div>
    );
};

export const MakePayment = () => {
    return (
        <div className="flex flex-col gap-l">
            <Text className="m-0 text-2xl font-bold max-sm:text-l">
                <FormattedMessage
                    id="_.credit_card.dashboard.make_payment.title"
                    defaultMessage="Make a payment"
                />
            </Text>
            <Text className="max-sm:text-sm mt-m text-m">
                <FormattedMessage
                    id="_.credit_card.dashboard.make_payment.description"
                    defaultMessage="Use the details below to make a payment on your iwoca Credit Card account."
                />
            </Text>
            <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                <Suspense fallback={<MainContentPending />}>
                    <PaymentAmounts />
                </Suspense>
            </ErrorBoundary>
            <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                <Suspense fallback={<MainContentPending />}>
                    <PaymentDetailsBox />
                </Suspense>
            </ErrorBoundary>
        </div>
    );
};
